import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import SupportButton from '../components/SupportButton';
import Header from '../components/Header';
import { 
  Lightbulb, 
  ArrowRight,
  Loader2,
  Phone,
  Mail,
  Users,
  Database,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for use cases
const USE_CASE_ICONS = {
  'hubspot-phone-formatting': Phone,
  'crm-migration-cleanup': Database,
  'email-list-hygiene': Mail,
  'sales-lead-quality': Users,
};

const UseCasesListPage = () => {
  const [useCases, setUseCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUseCases();
  }, []);

  const fetchUseCases = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/use-cases`);
      setUseCases(response.data.use_cases || []);
    } catch (error) {
      console.error('Failed to fetch use cases:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Shared Header */}
      <Header />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 rounded-full text-teal-700 text-sm font-medium mb-4">
            <Lightbulb className="w-4 h-4" />
            Use Cases
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-slate-900 mb-4">
            How Teams Use CRM Data Cleaner
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Discover how sales teams, marketers, and CRM admins use our tools to clean their data and improve their workflows.
          </p>
        </div>

        {/* Use Cases Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : useCases.length === 0 ? (
          <div className="text-center py-12">
            <Lightbulb className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No use cases published yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((useCase) => {
              const IconComponent = USE_CASE_ICONS[useCase.slug] || Lightbulb;
              
              return (
                <Link
                  key={useCase.use_case_id}
                  to={`/use-case/${useCase.slug}`}
                  className="group bg-white rounded-2xl border border-slate-200 p-6 hover:border-indigo-200 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-600 group-hover:text-white transition-colors">
                    <IconComponent className="w-6 h-6 text-teal-600 group-hover:text-white" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors">
                    {useCase.title}
                  </h3>
                  
                  <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                    {useCase.subtitle || (useCase.hero_description ? useCase.hero_description.slice(0, 150) + '...' : 'Learn how to use CRM Data Cleaner for this use case.')}
                  </p>
                  
                  <div className="flex items-center text-indigo-600 font-medium text-sm group-hover:gap-2 transition-all">
                    Read more
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-8 sm:p-12 text-center text-white">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Ready to Clean Your CRM Data?
          </h2>
          <p className="text-indigo-100 text-lg mb-8 max-w-xl mx-auto">
            Join thousands of sales teams who trust CRM Data Cleaner to maintain pristine databases.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button className="h-12 px-8 bg-white text-indigo-600 hover:bg-indigo-50">
                Start Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/tools/csv-doctor">
              <Button variant="outline" className="h-12 px-8 border-white text-white hover:bg-white/10">
                Try CSV Doctor Free
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <SupportButton />
    </div>
  );
};

export default UseCasesListPage;
